import { Auth } from "aws-amplify";
import { Base64 } from 'js-base64';

export interface RawAppSyncSubscriptionClientOptions {
  graphqlEndpoint: string;
  realtimeEndpoint: string;
  graphQLHost: string;
}

interface SubscribeParams {
  query: string;
  variables: Record<string, any>;
}

interface SubscribeCallbacks {
  next: (data: any) => void;
  error?: (error: any) => void;
}

interface SubscriptionEntry {
  callbacks: SubscribeCallbacks;
  query: string;
  variables: Record<string, any>;
}

// Called after the socket has reconnected AND every surviving subscription
// has been restarted. Consumers use it to refetch whatever was published
// while the socket was down — AppSync has no replay, so anything sent
// during the gap is gone unless the consumer goes and fetches it.
export type ReconnectListener = () => void;

// Exported for the tests, which assert the retry and timeout behaviour
// these values define rather than hard-coding their own copies.
export const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
export const RECONNECT_BACKOFF_MULTIPLIER = 2;
const KEEP_ALIVE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes without a keep-alive triggers reconnect
// A socket that opens but never acknowledges is the classic post-sleep
// half-open connection: no error, no close, no data. Without a deadline
// the connect promise would never settle and the reconnect loop would
// wait on it forever.
export const CONNECT_TIMEOUT_MS = 15000;

export class RawAppSyncSubscriptionClient {
  graphqlEndpoint: string;
  realtimeEndpoint: string;
  graphQLHost: string;
  ws: WebSocket | null;
  subscriptionId: number;
  private subscriptions: Map<string, SubscriptionEntry>;
  private reconnectDelayMs: number;
  // True for the WHOLE retry loop, not just one attempt — see
  // scheduleReconnect for why that matters.
  private reconnecting: boolean;
  private keepAliveTimerId: ReturnType<typeof setTimeout> | null;
  private reconnectTimerId: ReturnType<typeof setTimeout> | null;
  private intentionallyClosed: boolean;
  // Fails the in-flight connect attempt, if there is one. Held on the
  // instance so disconnect() can settle a pending attempt rather than
  // leaving its promise hanging.
  private failPendingConnect: ((reason: Error) => void) | null;
  private reconnectListeners: Set<ReconnectListener>;

  constructor({ graphqlEndpoint, realtimeEndpoint, graphQLHost }: RawAppSyncSubscriptionClientOptions) {
    this.graphqlEndpoint = graphqlEndpoint;
    this.realtimeEndpoint = realtimeEndpoint;
    this.graphQLHost = graphQLHost;
    this.ws = null;
    this.subscriptionId = 1;
    this.subscriptions = new Map();
    this.reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
    this.reconnecting = false;
    this.keepAliveTimerId = null;
    this.reconnectTimerId = null;
    this.intentionallyClosed = false;
    this.failPendingConnect = null;
    this.reconnectListeners = new Set();
  }

  // Connect
  // --------
  // Opens the realtime socket and resolves once AppSync has acknowledged
  // it. The promise is guaranteed to settle: it resolves on
  // connection_ack and rejects on connection_error, on a socket error, on
  // a close that arrives before the ack, and on CONNECT_TIMEOUT_MS.
  //
  // That guarantee is the whole point. The previous version settled only
  // on ack / connection_error / error, so a plain close — a server-side
  // teardown, or a socket that died while the machine slept — left the
  // promise pending forever. The reconnect loop awaits this promise, so
  // one such attempt wedged `reconnecting` true and silently disabled
  // reconnection for the rest of the session.
  async connect(): Promise<void> {
    const token = await this.getTokenSafely();
    if (!token) {
      throw new Error("Cannot connect: authentication token is unavailable");
    }

    const headers = {
      host: this.graphQLHost,
      Authorization: token
    };

    const encodedHeader = Base64.encodeURI(JSON.stringify(headers));

    // WebSocket subprotocols must include:
    const protocols = [
      "graphql-ws",
      "header-" + encodedHeader
    ];

    this.intentionallyClosed = false;
    const socket = new WebSocket(this.realtimeEndpoint, protocols);
    this.ws = socket;

    return new Promise<void>((resolve, reject) => {
      let attemptSettled = false;
      let connectTimeoutId: ReturnType<typeof setTimeout> | null = null;

      const settleAttempt = (): boolean => {
        if (attemptSettled) return false;
        attemptSettled = true;
        if (connectTimeoutId !== null) {
          clearTimeout(connectTimeoutId);
          connectTimeoutId = null;
        }
        socket.removeEventListener("message", connectionAckHandler);
        if (this.failPendingConnect === failAttempt) {
          this.failPendingConnect = null;
        }
        return true;
      };

      const succeedAttempt = (): void => {
        if (settleAttempt()) resolve();
      };

      const failAttempt = (reason: Error): void => {
        if (settleAttempt()) reject(reason);
      };

      this.failPendingConnect = failAttempt;

      connectTimeoutId = setTimeout(() => {
        failAttempt(
          new Error(`Timed out after ${CONNECT_TIMEOUT_MS}ms waiting for connection_ack`)
        );
        // Drop the half-open socket rather than leaving it as this.ws,
        // where it would look connected and swallow every start frame.
        socket.close();
      }, CONNECT_TIMEOUT_MS);

      socket.onopen = () => {
        socket.send(JSON.stringify({ type: "connection_init" }));
      };

      socket.onerror = (errorEvent: Event) => {
        failAttempt(
          new Error("WebSocket error: " + ((errorEvent as any).message ?? "connection failed"))
        );
      };

      socket.onmessage = (event: MessageEvent) => {
        this.handleMessage(event);
      };

      socket.onclose = () => {
        // A socket that has already been superseded by a newer attempt
        // must not disturb the live connection.
        if (this.ws !== socket) return;
        this.clearKeepAliveTimer();

        if (!attemptSettled) {
          // Closed before the ack: this is a FAILED ATTEMPT, not a lost
          // connection. Settle the promise and let whoever awaited
          // connect() decide what happens next — the reconnect loop
          // retries from its own catch, so scheduling here too would
          // only race it.
          failAttempt(new Error("WebSocket closed before the connection was acknowledged"));
          return;
        }

        if (!this.intentionallyClosed) {
          this.scheduleReconnect();
        }
      };

      // One-time listener for the handshake result. Ongoing messages are
      // handled by onmessage above.
      const connectionAckHandler = (event: MessageEvent) => {
        const message = this.parseMessage(event);
        if (!message) return;

        if (message.type === "connection_ack") {
          this.resetKeepAliveTimer();
          this.reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
          succeedAttempt();
        } else if (message.type === "connection_error") {
          failAttempt(new Error("Connection error: " + JSON.stringify(message.payload)));
        }
      };
      socket.addEventListener("message", connectionAckHandler);
    });
  }

  // On Reconnected
  // ---------------
  // Register a listener fired after every successful reconnect, once the
  // subscriptions have been restarted. Returns a disposer — callers MUST
  // call it when they tear down, or the listener outlives its owner.
  onReconnected(listener: ReconnectListener): () => void {
    this.reconnectListeners.add(listener);
    return () => {
      this.reconnectListeners.delete(listener);
    };
  }

  private notifyReconnected(): void {
    this.reconnectListeners.forEach((listener) => {
      try {
        listener();
      } catch (listenerError) {
        // One bad listener must not stop the others from catching up.
        console.error("Reconnect listener failed:", listenerError);
      }
    });
  }

  // Parse a socket frame, tolerating malformed payloads. An unguarded
  // JSON.parse inside a WebSocket event handler turns one bad frame into
  // an uncaught exception.
  private parseMessage(event: MessageEvent): any | null {
    try {
      return JSON.parse(event.data);
    } catch (parseError) {
      console.error("Ignoring unparseable WebSocket frame:", parseError);
      return null;
    }
  }

  // Central message handler — routes messages to the correct subscription callback
  private handleMessage(event: MessageEvent): void {
    const msg = this.parseMessage(event);
    if (!msg) return;

    switch (msg.type) {
      case "data": {
        const entry = this.subscriptions.get(msg.id);
        if (entry) {
          entry.callbacks.next?.(msg.payload.data);
        }
        break;
      }
      case "error": {
        const entry = this.subscriptions.get(msg.id);
        if (entry) {
          // Terminal for THIS subscription: AppSync stops delivering on
          // that id and will not restart it on its own. Drop the entry so
          // resubscribeAll doesn't keep re-issuing a start frame the
          // server has already rejected, then hand the consumer the error
          // so it can clear its handle and re-open deliberately.
          this.subscriptions.delete(msg.id);
          entry.callbacks.error?.(msg.payload);
        } else {
          console.error("Subscription error (no matching subscription):", msg.payload);
        }
        break;
      }
      case "ka": {
        // Keep-alive from AppSync — reset the timer
        this.resetKeepAliveTimer();
        break;
      }
      case "connection_ack": {
        // Already handled during connect, but reset timer in case of reconnect
        this.resetKeepAliveTimer();
        break;
      }
      default:
        break;
    }
  }

  async subscribe({ query, variables }: SubscribeParams): Promise<{ subscribe: (callbacks: SubscribeCallbacks) => { unsubscribe: () => void } }> {
    const subscriptionId = String(this.subscriptionId++);

    const token = await this.getTokenSafely();
    if (!token) {
      throw new Error("Cannot subscribe: authentication token is unavailable");
    }

    // Wire the subscribe function that the caller will invoke with their callbacks
    const subscribe = ({ next, error }: SubscribeCallbacks) => {
      // Register this subscription in the map BEFORE sending the start message
      // This ensures no messages are missed due to a race condition
      const entry: SubscriptionEntry = {
        callbacks: { next, error },
        query,
        variables,
      };
      this.subscriptions.set(subscriptionId, entry);

      // Now send the start message to AppSync
      this.sendStartMessage(subscriptionId, query, variables, token);

      return {
        unsubscribe: () => {
          this.subscriptions.delete(subscriptionId);
          this.sendStopMessage(subscriptionId);
        },
      };
    };

    return { subscribe };
  }

  // Send a start message for a subscription
  private sendStartMessage(subscriptionId: string, query: string, variables: Record<string, any>, token: string): void {
    if (!this.isSocketOpen()) {
      // Not an error worth shouting about: the entry is already in
      // this.subscriptions, and resubscribeAll re-issues every start
      // frame as soon as the socket is back. Sending now would throw
      // InvalidStateError on a CONNECTING or CLOSED socket.
      console.warn(
        `Socket not open — deferring subscription ${subscriptionId} to the next reconnect`
      );
      return;
    }

    const data = { query, variables };
    const dataString = JSON.stringify(data);

    const startPayload = {
      id: subscriptionId,
      type: "start",
      payload: {
        data: dataString,
        extensions: {
          authorization: {
            host: this.graphQLHost,
            Authorization: token
          }
        }
      },
    };

    try {
      this.ws?.send(JSON.stringify(startPayload));
    } catch (sendError) {
      console.error("Failed to send subscription start message:", sendError);
    }
  }

  // Tell AppSync to stop publishing on a subscription. A closed socket
  // needs no stop frame — the server drops every subscription on it.
  private sendStopMessage(subscriptionId: string): void {
    if (!this.isSocketOpen()) return;
    try {
      this.ws?.send(JSON.stringify({ id: subscriptionId, type: "stop" }));
    } catch (sendError) {
      console.warn("Failed to send unsubscribe:", sendError);
    }
  }

  private isSocketOpen(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  // Disconnect and clean up
  disconnect(): void {
    this.intentionallyClosed = true;
    this.clearKeepAliveTimer();
    if (this.reconnectTimerId !== null) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }
    this.reconnecting = false;
    this.subscriptions.clear();
    this.reconnectListeners.clear();

    // Settle a connect attempt that is still in flight, so its awaiter
    // isn't left hanging on a promise that can now never resolve.
    const failPendingConnect = this.failPendingConnect;
    this.failPendingConnect = null;
    failPendingConnect?.(new Error("Client disconnected before the connection was acknowledged"));

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  // Keep-alive timer management
  // If we don't receive a keep-alive within the timeout, assume the connection is stale
  private resetKeepAliveTimer(): void {
    this.clearKeepAliveTimer();
    this.keepAliveTimerId = setTimeout(() => {
      console.warn("Keep-alive timeout — connection appears stale, reconnecting");
      if (this.ws) {
        this.ws.close();
        // onclose handler will trigger reconnect
      }
    }, KEEP_ALIVE_TIMEOUT_MS);
  }

  private clearKeepAliveTimer(): void {
    if (this.keepAliveTimerId) {
      clearTimeout(this.keepAliveTimerId);
      this.keepAliveTimerId = null;
    }
  }

  // Reconnection with exponential backoff
  //
  // `reconnecting` is held for the entire retry loop rather than for a
  // single attempt. Any stray close event arriving mid-loop is then a
  // harmless no-op instead of starting a second, competing loop.
  private scheduleReconnect(): void {
    if (this.reconnecting || this.intentionallyClosed) return;
    this.reconnecting = true;
    this.queueReconnectAttempt();
  }

  private queueReconnectAttempt(): void {
    if (this.intentionallyClosed) {
      this.reconnecting = false;
      return;
    }

    const delay = this.reconnectDelayMs;
    this.reconnectDelayMs = Math.min(
      this.reconnectDelayMs * RECONNECT_BACKOFF_MULTIPLIER,
      MAX_RECONNECT_DELAY_MS
    );

    console.warn(`WebSocket disconnected. Reconnecting in ${delay}ms...`);

    this.reconnectTimerId = setTimeout(async () => {
      this.reconnectTimerId = null;
      if (this.intentionallyClosed) {
        this.reconnecting = false;
        return;
      }

      try {
        await this.connect();
        await this.resubscribeAll();

        // A socket that died between the ack and the end of the restore
        // leaves us nominally connected with nothing running on it. The
        // close event that would normally trigger a retry was suppressed
        // by this very loop being in progress, so check explicitly and
        // route the failure back through the retry path.
        if (!this.isSocketOpen()) {
          throw new Error("WebSocket closed while subscriptions were being restored");
        }

        this.reconnecting = false;
        console.log("WebSocket reconnected and subscriptions restored");
        this.notifyReconnected();
      } catch (reconnectError) {
        console.error("Reconnection failed:", reconnectError);
        // Retry from HERE rather than relying on the failed socket's
        // onclose. An attempt that dies before the socket even exists —
        // getTokenSafely returning null because the network isn't back
        // yet after a laptop wake, say — produces no close event at all,
        // and the previous version simply stopped retrying for the rest
        // of the session.
        this.queueReconnectAttempt();
      }
    }, delay);
  }

  // Re-register all active subscriptions after a reconnect.
  //
  // Throws rather than returning quietly when the token is unavailable:
  // a "reconnected" socket with nothing subscribed on it delivers
  // nothing, and the caller must treat that as a failed attempt so the
  // retry loop keeps going.
  private async resubscribeAll(): Promise<void> {
    const token = await this.getTokenSafely();
    if (!token) {
      throw new Error("Cannot resubscribe: authentication token is unavailable");
    }

    this.subscriptions.forEach((entry, subscriptionId) => {
      this.sendStartMessage(subscriptionId, entry.query, entry.variables, token);
    });
  }

  async getTokenSafely(): Promise<string | null> {
    try {
      const session = await Auth.currentSession();
      // Use the id token, not the access token. AppSync resolvers on this
      // backend read tenant-scoped claims (custom:tenantId, custom:tier, email)
      // from $ctx.identity.claims; those claims live on the id token and are
      // absent from the access token. Subscriptions and queries must agree
      // on the token type or resolvers see different identities for the same
      // user across the two transports.
      return session.getIdToken().getJwtToken();
    } catch (err) {
      console.error("User is not signed in or token retrieval failed", err);
      return null;
    }
  }
}
